import React, { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Button,
  Spin,
  Modal,
  message,
  Empty,
  Form,
  Input,
  Select
} from "antd";
import { consultationService } from "../../../services/medicalConsultation";
import { InfoCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { findIndex, sortBy } from "lodash";
import { handlers } from './../../../helpers/helpers';
import { useTranslation } from "react-i18next";
import ConsultationDiagnosticExamDrawer from "./ConsultationDiagnosticExamDrawer";
import {
  buildExamOptions,
  filterExamOption,
  renderExamOptions,
  toggleGroupExamIds,
} from "./utils/examGroupingUtils";

const { TextArea } = Input;
const { Option } = Select;

const PrescriptionsModal = ({
  id,
  serviceId,
  serviceType,
  serviceDetail,
  patientData,
  isModalVisible,
  setIsModalVisible,
  medicalConsultationDetailId,
  countryCode,
  status,
  reevaluationState,
  countrySettings,
  medicalFormType,
  editingPrescription,
  setEditingPrescription,
  onSuccess,
  isConsultationSigned,
}) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerExamIds, setDrawerExamIds] = useState([]);
  const [visiblePopoverKey, setVisiblePopoverKey] = useState(null);

  const drawerRecord = useMemo(() => {
    if (!drawerExamIds || drawerExamIds.length === 0) return null;

    const primaryExamId = drawerExamIds[0];
    const selectedExam = serviceDetail?.find((exam) => exam.exam_id === primaryExamId);

    const siblingExams = drawerExamIds.slice(1).map(examId => {
      const exam = serviceDetail?.find((e) => e.exam_id === examId);
      return {
        admission_patient_exam: exam?.admission_patient_exam_id,
        exam_name: exam?.name,
        admission_patient_id: id,
        to_doctor: exam?.to_doctor,
        need_expert_sign: exam?.need_expert_sign,
        allow_diagnostics: exam?.allow_diagnostics,
      };
    });

    return {
      ...selectedExam,
      admission_patient_id: id,
      medical_consultation_id: null,
      admission_patient_exam: selectedExam?.admission_patient_exam_id,
      full_name: patientData?.full_name,
      document_number: patientData?.document_number,
      document_type: patientData?.document_type,
      exam_name: selectedExam?.name,
      isGroup: false,
      siblingExams,
    };
  }, [drawerExamIds, serviceDetail, id, patientData]);

  const handleViewExam = (examIds) => {
    const ids = Array.isArray(examIds) ? examIds : (examIds ? [examIds] : []);
    setDrawerExamIds(ids);
    setDrawerVisible(true);
    setVisiblePopoverKey(null);
  };
  const [loading, setLoading] = useState({
    prescriptions: false,
    sendForm: false,
  });
  const [prescriptions, setPrescriptions] = useState([]);
  const [selectedPrescriptions, setSelectedPrescriptions] = useState([]);
  const [filteredPrescriptions, setFilteredPrescriptions] = useState([]);
  const [addingDocument, setAddingDocument] = useState([]);
  const [newDocumentNames, setNewDocumentNames] = useState([]);
  const [selectedPrescription, setSelectedPrescription] = useState(countrySettings.prescriptionModal.prescriptionOnly[countryCode] ? 'prescription' : null);
  const disableConditions = {
    prescription: status?.status !== 'reproved_temporally',
  };
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const allowMultiSelect = countrySettings.prescriptionModal.allowMultiSelectExams?.[countryCode] || false;
  const showDocuments = countrySettings.prescriptionModal.showDocuments?.[countryCode] ?? true;
  const requireExamSelection = countrySettings.prescriptionModal.requireExamSelection?.[countryCode] ?? true;
  const shouldGroupExams = countrySettings.prescriptionModal.groupExams?.[countryCode] && allowMultiSelect;
  const prescriptionTypes = [
    {
      key: "prescription",
      disabled: disableConditions.prescription,
      label: t('medicalConsultations.prescriptionsModal.prescriptionLabel'),
    },
    {
      key: "medical_order",
      disabled: disableConditions.medical_order,
      label: t('medicalConsultations.prescriptionsModal.medicalOrderLabel'),
    }
  ];

  const fetchPrescriptions = useCallback(async (params) => {
    setLoading((loading) => { return { ...loading, prescriptions: true }; });
    const response = await consultationService.getPrescriptions(params);

    if (response && response.length > 0) {
      const sortByAlphabet = sortBy(response, ({ label }) => label.toLowerCase());
      setPrescriptions(sortByAlphabet);
      setFilteredPrescriptions(sortByAlphabet);
    } else {
      setPrescriptions([]);
      setFilteredPrescriptions([]);
    }

    setLoading((loading) => { return { ...loading, prescriptions: false }; });
  }, []);


  useEffect(() => {
    fetchPrescriptions({
      id: serviceId,
      type: serviceType,
      department_id: ['psychological_one', 'psychological_two'].includes(medicalFormType?.toString()?.toLowerCase()) ? 2 : 1
    });
  }, [fetchPrescriptions, medicalFormType, serviceId, serviceType]);

  useEffect(() => {
    if (isModalVisible.prescription && editingPrescription && prescriptions.length > 0) {
      const extraData = editingPrescription.extra_data;
      if (Array.isArray(extraData)) {
        const mapped = extraData.map((item) => {
          const master = prescriptions.find((prescription) => prescription.key === item.key);
          if (master) {
            const replacements = {};
            if (Array.isArray(item.inputs)) {
              item.inputs.forEach((input) => {
                replacements[input.key] = input.value;
              });
            }
            const inputs = master.template.match(/{{([^}]+)}}/g);
            let sanitizedExamId;
            if (allowMultiSelect) {
              sanitizedExamId = Array.isArray(item.examId) ? item.examId : (item.examId ? [item.examId] : []);
            } else {
              sanitizedExamId = Array.isArray(item.examId) ? item.examId[0] : item.examId;
            }

            return {
              key: item.key,
              inputs,
              label: item.name,
              presence_required: item.presence_required,
              template: master.template,
              replacements,
              inputType: master.input_type,
              documents: (item.documents || []).map((doc) => ({
                ...doc,
                exam_id: doc.exam_id || (Array.isArray(item.examId) ? item.examId[0] : item.examId) || null,
              })),
              exam_id: sanitizedExamId,
            };
          }
          return null;
        }).filter(Boolean);

        setSelectedPrescriptions(mapped);
        setSelectedPrescription(editingPrescription.prescription_type);

        const formValues = {};
        mapped.forEach((item) => {
          if (item.exam_id) {
            if (allowMultiSelect) {
              formValues[item.key] = Array.isArray(item.exam_id) ? item.exam_id : [item.exam_id];
            } else {
              formValues[item.key] = Array.isArray(item.exam_id) ? item.exam_id[0] : item.exam_id;
            }
          }
        });
        form.setFieldsValue(formValues);
      }
    }
  }, [isModalVisible.prescription, editingPrescription, prescriptions, form, allowMultiSelect]);

  const closeModal = () => {
    form.resetFields();
    setSelectedPrescriptions([]);
    setFilteredPrescriptions([...prescriptions]);
    setSelectedPrescription(countrySettings.prescriptionModal.prescriptionOnly[countryCode] ? 'prescription' : null);
    if (setEditingPrescription) {
      setEditingPrescription(null);
    }
    setIsModalVisible((isModalVisible) => {
      return { ...isModalVisible, prescription: false };
    });
  };

  const removeDocument = (item, document) => {
    const _selectedPrescriptions = selectedPrescriptions;
    const findPrescriptionIndex = _selectedPrescriptions.map(elem => elem['key']).indexOf(item.key);
    const documents = _selectedPrescriptions[findPrescriptionIndex]['documents'];
    const findDocument = documents.map(elem => elem['name']).indexOf(document.name);

    documents.splice(findDocument, 1);
    _selectedPrescriptions[findPrescriptionIndex]['documents'] = documents.length > 0 ? documents : [];
    setSelectedPrescriptions([..._selectedPrescriptions]);
  };

  const newDocument = (item) => {
    if (!addingDocument[item.key]) {
      setAddingDocument((addingDocument) => {
        return { ...addingDocument, [item.key]: true };
      });
    } else {
      if (newDocumentNames[item.key] && newDocumentNames[item.key] !== '') {
        const _selectedPrescriptions = selectedPrescriptions;
        const findPrescriptionIndex = _selectedPrescriptions.map(elem => elem['key']).indexOf(item.key);
        _selectedPrescriptions[findPrescriptionIndex]['documents'] = [
          ..._selectedPrescriptions[findPrescriptionIndex]['documents'],
          {
            name: newDocumentNames[item.key],
            exam_document_id: null,
            exam_id: null
          }
        ];
        setSelectedPrescriptions([..._selectedPrescriptions]);
        setAddingDocument((addingDocument) => {
          return { ...addingDocument, [item.key]: false };
        });
        setNewDocumentNames((newDocumentNames) => {
          return { ...newDocumentNames, [item.key]: null };
        });
      } else {
        message.error({
          content: "Debe ingresar el nombre del documento",
          style: {
            marginTop: "35vh"
          }
        });
      }
    }
  };

  const injectValues2Template = (event) => {
    const name = event.target.attributes["name"].value;
    const key = event.target.attributes["id"].value;
    const index = findIndex(selectedPrescriptions, { key });
    if (index !== -1) {
      const updatedPrescriptions = [...selectedPrescriptions];
      const replacements = { ...updatedPrescriptions[index].replacements };
      replacements[name] = event.target.value;
      updatedPrescriptions[index] = {
        ...updatedPrescriptions[index],
        replacements,
      };
      setSelectedPrescriptions(updatedPrescriptions);
    }
  };

  const addPrescription = (key, label, presence_required, template, inputType, documents) => {
    const checkIfPrescriptionExists = selectedPrescriptions ? selectedPrescriptions.filter((item) => item["key"] === key) : [];

    if (checkIfPrescriptionExists.length > 0) {
      message.info({
        content: `Ya has agregado esa ${t('medicalConsultations.prescriptionsModal.prescription').toLocaleLowerCase()}`,
        style: {
          marginTop: "40vh"
        }
      });
    } else if (countrySettings.prescriptionModal.onlyOnePrescription[countryCode] && selectedPrescriptions.length > 0) {
      message.info({
        content: t('medicalConsultations.prescriptionsModal.onlyOnePrescription'),
        style: {
          marginTop: "40vh"
        }
      });
    } else {
      const inputs = template.match(/{{([^}]+)}}/g);
      const initialReplacements = {};
      inputs?.forEach(input => {
        const content = input.match(/\{\{([^}]+)\}\}/)[1];
        const contentWithoutFirstWord = content.split(' ').slice(1).join(' ');
        initialReplacements[content.split(' ')[0]] = contentWithoutFirstWord;
      });
      setSelectedPrescriptions((selectedPrescriptions) => [
        ...selectedPrescriptions,
        {
          key,
          inputs,
          label,
          presence_required,
          template,
          replacements: initialReplacements,
          inputType,
          documents: (documents || []).map(documentItem => ({ ...documentItem, exam_id: null })),
          exam_id: allowMultiSelect ? [] : undefined
        }
      ]);
    }
  };

  const removePrescription = (key) => {
    const _selectedPrescriptions = selectedPrescriptions.filter((item) => item["key"] !== key);
    setSelectedPrescriptions([..._selectedPrescriptions]);
  };


  const searchPrescriptions = (event) => {
    let inputValue = event.target.value;

    if (inputValue) {
      inputValue = inputValue.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

      const filterList = prescriptions.filter((item) => {
        return (
          item.label
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .includes(inputValue)
        );
      });

      setFilteredPrescriptions([...filterList]);
    } else {
      setFilteredPrescriptions([...prescriptions]);
    }
  };

  const examOptions = useMemo(
    () => buildExamOptions(serviceDetail, shouldGroupExams),
    [serviceDetail, shouldGroupExams]
  );

  const handleSetExamId = (prescriptionIndex, examId) => {
    const _selectedPrescriptions = selectedPrescriptions;

    _selectedPrescriptions[prescriptionIndex] = {
      ..._selectedPrescriptions[prescriptionIndex],
      exam_id: examId
    };

    setSelectedPrescriptions([..._selectedPrescriptions]);
  };

  const handleClickGroupTitle = (prescriptionIndex, prescriptionItem, groupExams) => {
    const selectedExamIds = Array.isArray(prescriptionItem.exam_id) ? prescriptionItem.exam_id : [];
    const updatedExamIds = toggleGroupExamIds(selectedExamIds, groupExams);

    form.setFieldsValue({ [prescriptionItem.key]: updatedExamIds });
    handleSetExamId(prescriptionIndex, updatedExamIds);
  };

  const handleSetDocExamId = (prescriptionIndex, documentIndex, examId) => {
    const _selectedPrescriptions = [...selectedPrescriptions];
    const item = _selectedPrescriptions[prescriptionIndex];
    if (item && item.documents && item.documents[documentIndex]) {
      item.documents[documentIndex] = {
        ...item.documents[documentIndex],
        exam_id: examId,
      };
      setSelectedPrescriptions(_selectedPrescriptions);
    }
  };

  const createPrescription = async () => {
    const prescriptionLabel = t('medicalConsultations.prescriptionsModal.prescription').toLocaleLowerCase();
    const patientLabel = t('medicalConsultations.prescriptionsModal.patientTitle').toLocaleLowerCase();

    const willDelayNotification = countrySettings.prescriptionModal.delayNotificationUntilSign[countryCode] && !isConsultationSigned;

    const confirmMessage = willDelayNotification
      ? `La ${prescriptionLabel} se generará en estado borrador y se enviará al ${patientLabel} por correo electrónico una vez que la consulta sea firmada, ¿Está seguro que desea generar una nueva ${prescriptionLabel}?`
      : `La ${prescriptionLabel} que se generará le llegará al ${patientLabel} a través de correo electrónico, ¿Está seguro que desea generar una nueva ${prescriptionLabel}?`;

    if (!editingPrescription && selectedPrescription === 'prescription' && !window.confirm(confirmMessage)) {
      return;
    }

    let prescriptionType = '';

    if (countrySettings.prescriptionModal.prescriptionOnly[countryCode]) {
      prescriptionType = prescriptionTypes[0];
    } else {
      prescriptionType = prescriptionTypes.find((type) => type.key === selectedPrescription);
    }

    form.validateFields();
    const exams = [];

    const replaceKeyName = selectedPrescriptions.map((selectedPrescription) => {
      const replacements = selectedPrescription.replacements;

      const processedDocuments = showDocuments ? (selectedPrescription.documents?.map(documentItem => {
        let documentExamId = documentItem.exam_id;
        if (!documentExamId) {
          if (Array.isArray(selectedPrescription.exam_id)) {
            documentExamId = selectedPrescription.exam_id[0] || null;
          } else {
            documentExamId = selectedPrescription.exam_id || null;
          }
        }
        return {
          name: documentItem.name,
          exam_document_id: documentItem.exam_document_id,
          exam_id: documentExamId,
          new_document: documentItem.new_document,
        };
      }) || []) : [];

      let item = {
        key: selectedPrescription.key,
        name: selectedPrescription.label,
        presence_required: selectedPrescription.presence_required,
        documents: processedDocuments,
        examId: selectedPrescription.exam_id
      };

      if (Array.isArray(selectedPrescription.exam_id)) {
        selectedPrescription.exam_id.forEach(examId => {
          if (!exams.includes(examId)) {
            exams.push(examId);
          }
        });
      } else if (selectedPrescription.exam_id) {
        if (!exams.includes(selectedPrescription.exam_id)) {
          exams.push(selectedPrescription.exam_id);
        }
      }

      let inputs = [];

      for (let replacement of Object.keys(replacements)) {
        let input = {};
        const key = replacement
          .replace(/\{{|\}}/gi, "")
          .replace("-", "_")
          .replace(".", "")
          .replace(":", "");
        input["key"] = key;
        input["value"] = replacements[replacement];
        inputs.push(input);
      }

      item["inputs"] = inputs;

      return item;
    });

    let totalDocuments = 0;

    replaceKeyName.forEach((prescription) => {
      totalDocuments += prescription?.documents?.length || 0;
    });

    let params = {
      admissionPatientId: id,
      medicalConsultationDetailId: medicalConsultationDetailId,
      prescriptions: replaceKeyName,
      type: selectedPrescription,
      exams,
      ...(editingPrescription && { medicalPrescriptionId: editingPrescription.id }),
    };

    try {
      setLoading((loading) => { return { ...loading, sendForm: true }; });

      let response = await consultationService.generatePrescription(params);

      if (response["pdf"]) {
        let signParams = {
          medicalConsultationDetailId: medicalConsultationDetailId,
          pdf: response["pdf"],
          prescriptionItems: replaceKeyName,
          type: selectedPrescription,
          totalDocuments,
          exams,
          patient: {
            full_name: patientData.full_name,
            document_number: patientData.document_number,
          },
          ...(editingPrescription && { id: editingPrescription.id }),
        };
        response = await consultationService.signMedicalPrescription(signParams);

        const successWord = editingPrescription ? "actualizada" : "generada";

        if (countrySettings.prescriptionModal.notifyPatient[countryCode] && response["success"] === true && selectedPrescription === "prescription") {
          response = await consultationService.notifyReevaluation({
            medicalConsultationDetailId,
            ...(countrySettings.prescriptionModal.sendObservated[countryCode]
              && { observated: countrySettings.prescriptionModal.sendObservated[countryCode] })
          });
          message.success({
            content: `${prescriptionType.label} ${successWord} exitosamente.`,
            style: {
              marginTop: "35vh",
            },
          });
          if (response.patientResponse.responseEmail === 400 && response.patientResponse.responseWhatsapp === 400) {
            message.error({
              content: `El ${t('medicalConsultations.prescriptionsModal.patientTitle').toLocaleLowerCase()} no tiene método de contacto. Es necesario entregar instrucciones.`,
              style: {
                marginTop: "35vh"
              }
            });
          }
          closeModal();
          if (onSuccess) {
            onSuccess();
          }
        } else if (response["success"] === true) {
          message.success({
            content: `${prescriptionType.label} ${successWord} exitosamente`,
            style: {
              marginTop: "35vh",
            },
          });
          closeModal();
          if (onSuccess) {
            onSuccess();
          }
        } else {
          message.error({
            content: `Ha ocurrido un error al generar la ${prescriptionType.label.toLocaleLowerCase()}. Intente nuevamente.`,
            style: {
              marginTop: "35vh"
            }
          });
        }
      }
    } catch (err) {
      const errorMessage = handlers.isJSON(err) ? JSON.parse(err) : err;
      console.error(`Error interno: ${errorMessage}`);
      message.error({
        content: `Error interno: ${errorMessage && errorMessage.message} al generar ${t('medicalConsultations.prescriptionsModal.prescription').toLocaleLowerCase()}`,
        style: {
          marginTop: "35vh"
        }
      });
    } finally {
      setLoading((loading) => { return { ...loading, sendForm: false }; });
    }
  };

  const updateNewDocumentValue = (event, item) => {
    setNewDocumentNames((newDocumentNames) => {
      return { ...newDocumentNames, [item.key]: event.target.value };
    });
  };

  const renderPrescriptionExamOptions = (item, prescriptionIndex) => renderExamOptions({
    examOptions,
    selectedExamIds: Array.isArray(item.exam_id) ? item.exam_id : [],
    onGroupTitleClick: (groupExams) => handleClickGroupTitle(prescriptionIndex, item, groupExams),
  });

  const renderTemplate = (template, item) => {
    const parts = template.split(/(\{\{[^}]+\}\})/g);

    return (
      <div className="prescription-template">
        {parts.map((part, index) => {
          const match = part.match(/^\{\{([^}]+)\}\}$/);

          if (match) {
            const content = match[1];
            const name = content.split(" ")[0];

            if (item.inputType === "free" || item.key === "free_text_v2") {
              return (
                <TextArea
                  key={index}
                  required
                  name={name}
                  value={item.replacements?.[name] || ""}
                  className="cmt-input template-input-textarea"
                  template={item.template}
                  id={item.key}
                  onChange={injectValues2Template}
                />
              );
            }

            return (
              <Input
                key={index}
                required
                size="small"
                name={name}
                value={item.replacements?.[name] || ""}
                className="cmt-input template-input-text"
                template={item.template}
                id={item.key}
                onChange={injectValues2Template}
              />
            );
          }

          return part.split(' ').map((word, wordIndex) => (
            word ?
              <span key={`${index}-${wordIndex}`}>
                {word}
              </span>
              : " "
          ));
        })}
      </div>
    );
  };

  return (
    <Modal
      visible={isModalVisible.prescription}
      onCancel={closeModal}
      centered
      closable
      footer={false}
      width={"95vw"}
      className="prescriptions-modal"
    >
      <Row justify="center">
        {loading.prescriptions ? (
          <Spin className="custom-spinner mt-50" />
        ) : (
          <Fragment>
            <Col lg={7} xs={24} className="column-list p-40">
              <Row>
                <Col lg={24} xs={24}>
                  <h2 className="mb-40">{t('medicalConsultations.prescriptionsModal.title')}</h2>
                </Col>
                <Col lg={24} xs={24}>
                  <Row>
                    <Col lg={24} xs={24}>
                      <p className="mb-5">{t('medicalConsultations.prescriptionsModal.searchPrescription')}</p>
                    </Col>
                    <Col lg={24} xs={24}>
                      <Input
                        onChange={searchPrescriptions}
                        className="cmt-input-icon"
                        placeholder={`Nombre de la ${t('medicalConsultations.prescriptionsModal.prescription').toLocaleLowerCase()}`}
                        style={{ width: '100%' }}
                        size="large"
                        allowClear={() => setFilteredPrescriptions([...prescriptions])}
                        prefix={
                          <i className="icon-system-search" style={{ color: '#BEBEBE' }} />
                        }
                      />
                    </Col>
                  </Row>
                </Col>
                <Col lg={24} xs={24} className="prescription-items mt-30">
                  {
                    filteredPrescriptions.length > 0 ?
                      filteredPrescriptions.map((item, index) => {
                        return (
                          <p
                            key={index}
                            onClick={() =>
                              addPrescription(
                                item.key,
                                item.label,
                                item.presence_required,
                                item.template,
                                item.input_type,
                                item.documents
                              )
                            }
                            className="text-button pl-10 pr-10 pb-5 pt-5 mb-0"
                          >
                            {item.label}
                          </p>
                        );
                      })
                      :
                      <p className="p-10">No hay resultados</p>
                  }
                </Col>
              </Row>
            </Col>
            <Col lg={17} xs={24} className="column-edit p-40">
              <Row>
                <Col lg={24} xs={24}>
                  <p>
                    {t('medicalConsultations.prescriptionsModal.patientTitle')}:
                    <strong className="ml-10">
                      {patientData.full_name || ''}
                    </strong>
                  </p>
                </Col>
                <Col lg={24} xs={24}>
                  {
                    selectedPrescriptions?.length === 0 ? (
                      <Row justify="center">
                        <Empty
                          className="mt-30"
                          description={
                            <span style={{ fontSize: 14 }}>
                              {t('medicalConsultations.prescriptionsModal.prescriptionsDisclaimer')}
                            </span>
                          }
                          image={<InfoCircleOutlined style={{ fontSize: 50 }} />}
                        />
                      </Row>
                    ) : (
                      <Form
                        form={form}
                        onFinish={createPrescription}
                        size="large"
                      >
                        <Row gutter={[0, 5]}>
                          {
                            selectedPrescriptions?.map((item, index) => {
                              return (
                                <Col
                                  key={index}
                                  lg={24}
                                  xs={24}
                                  className="mb-20"
                                >
                                  <Row className="prescription-form-wrapper p-20">
                                    <Col lg={23} xs={22}>
                                      <p className="bold mb-10">{item.label}</p>
                                    </Col>
                                    <Col lg={1} xs={2} style={{ display: "flex", alignItems: "flex-start", justifyContent: "end" }}>
                                      <Button
                                        onClick={() => removePrescription(item.key)}
                                        className="cmt-button secondary non-outline"
                                        type="primary"
                                        htmlType="button"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 148.61 189.36"
                                        >
                                          <g id="Capa_2" dataname="Capa 2">
                                            <g id="Capa_1-2" dataname="Capa 1">
                                              <path d="M47.16,21.25c0-3.94-.14-7.24,0-10.53C47.49,4.64,51.6.29,57.66.17q16.5-.33,33,0c6.37.11,10.48,4.42,10.78,10.79.15,3.28,0,6.58,0,10.4,11.53,0,22.48-.06,33.43,0,7.49,0,12.84,3.93,13.53,10a92.45,92.45,0,0,1,.07,10.16H.13c0-3.65-.34-7.14.08-10.54.69-5.58,5.89-9.6,12.18-9.65C23.71,21.19,35,21.25,47.16,21.25Zm46.89-.2c0-3.38,0-6.19,0-9C94,9,92.36,7.48,89.32,7.48c-10,0-20-.16-29.93.17-1.52,0-3.93,2.14-4.25,3.64-.67,3.1-.2,6.43-.2,9.76Z" />
                                              <path d="M15.42,54.5v4.63q0,58.15,0,116.31c0,10.25,4.24,13.91,16.09,13.91q42.55,0,85.09,0c12.36,0,16.25-3.36,16.25-14.11q0-57.93,0-115.88V54.5Z" />
                                            </g>
                                          </g>
                                        </svg>
                                      </Button>
                                    </Col>
                                    <Col lg={24} xs={24}>
                                      {renderTemplate(item.template, item)}
                                    </Col>
                                    <Col lg={24} xs={24} className="mt-20">
                                      <Row justify="space-between">
                                        <Col lg={17} xs={24}>
                                          <Row align="middle">
                                            {showDocuments && (
                                              <>
                                                <Col lg={24} xs={24}>
                                                  <p className="bold mb-5">
                                                    {t('medicalConsultations.prescriptionsModal.stepOne')}
                                                  </p>
                                                </Col>
                                                {
                                                  item.documents?.map((documentItem, documentIndex) => {
                                                    return (
                                                      <Col key={documentIndex} className="prescription-document mr-15 mb-10">
                                                        <Row align="middle">
                                                          <p className="mb-0">
                                                            {documentItem['name'] ? documentItem['name'] : documentItem['file_name']}
                                                          </p>
                                                          {allowMultiSelect && item.exam_id && item.exam_id.length > 1 && (
                                                            <Select
                                                              placeholder="Asociar a examen"
                                                              className="cmt-select select-doc-exam ml-10"
                                                              size="small"
                                                              value={documentItem.exam_id || undefined}
                                                              onChange={(value) => handleSetDocExamId(index, documentIndex, value)}
                                                            >
                                                              {item.exam_id.map((examId) => {
                                                                const examOption = serviceDetail.find(examOptionItem => examOptionItem.exam_id === examId);
                                                                return (
                                                                  <Option key={examId} value={examId}>
                                                                    {examOption?.name || `ID: ${examId}`}
                                                                  </Option>
                                                                );
                                                              })}
                                                            </Select>
                                                          )}
                                                          <Button onClick={() => removeDocument(item, documentItem)} size='small' type="text" className="mb-0 ml-5 delete-doc-button" htmlType="button">
                                                            <CloseOutlined />
                                                          </Button>
                                                        </Row>
                                                      </Col>
                                                    );
                                                  })
                                                }
                                                {
                                                  addingDocument[item.key] && (
                                                    <Row>
                                                      <Col>
                                                        <Input
                                                          size="small"
                                                          style={{
                                                            width: 100,
                                                            marginRight: 5,
                                                          }}
                                                          className="cmt-input"
                                                          onChange={event => updateNewDocumentValue(event, item)}
                                                        />
                                                      </Col>
                                                      <Col>
                                                        <Button
                                                          className="cmt-button primary outline pl-10 pr-10"
                                                          type="primary"
                                                          size="small"
                                                          onClick={() => newDocument(item)}
                                                          htmlType="button"
                                                        >
                                                          <i className="icon-system-plus mr-5" /> Agregar
                                                        </Button>
                                                      </Col>
                                                    </Row>
                                                  )
                                                }
                                                {
                                                  !addingDocument[item.key] && (
                                                    <Col>
                                                      <Button
                                                        className="cmt-button primary outline pl-10 pr-10"
                                                        type="primary"
                                                        size="small"
                                                        onClick={() => newDocument(item)}
                                                        htmlType="button"
                                                      >
                                                        <i className="icon-system-plus mr-5" /> Agregar
                                                      </Button>
                                                    </Col>
                                                  )
                                                }
                                              </>
                                            )}
                                          </Row>
                                          <Row className="mt-10">
                                            <Col lg={24} xs={24}>
                                              <p className="bold">
                                                {showDocuments ? t('medicalConsultations.prescriptionsModal.stepTwo') : t('medicalConsultations.prescriptionsModal.selectAssociatedExam')}
                                              </p>
                                            </Col>
                                            <Col lg={20} xs={24}>
                                              {
                                                serviceDetail &&
                                                <Row gutter={8} align="middle">
                                                  <Col flex="1">
                                                    <Form.Item
                                                      name={item.key}
                                                      rules={[{
                                                        required: requireExamSelection,
                                                        message: 'Debes seleccionar examen'
                                                      }]}
                                                      className="mb-0"
                                                    >
                                                      <Select
                                                        mode={allowMultiSelect ? "multiple" : undefined}
                                                        placeholder="Seleccione examen"
                                                        className="cmt-select"
                                                        popupClassName={shouldGroupExams ? "grouped-exams-dropdown" : undefined}
                                                        onChange={(value) => handleSetExamId(index, value)}
                                                        id="selectExam"
                                                        showSearch
                                                        filterOption={filterExamOption}
                                                      >
                                                        {renderPrescriptionExamOptions(item, index)}
                                                      </Select>
                                                    </Form.Item>
                                                  </Col>
                                                  {allowMultiSelect && (
                                                  <Col>
                                                    <Button
                                                      disabled={!item.exam_id || (Array.isArray(item.exam_id) && item.exam_id.length === 0)}
                                                      onClick={() => handleViewExam(item.exam_id)}
                                                      type="primary"
                                                      htmlType="button"
                                                      className="cmt-button secondary outline call-patient-button"
                                                    >
                                                      Ver examen
                                                    </Button>
                                                  </Col>
                                                  )}
                                                </Row>
                                              }
                                            </Col>
                                          </Row>
                                        </Col>
                                        <Col lg={7} xs={24} style={{ display: 'flex', justifyContent: 'end', alignItems: 'end' }}>
                                          {item.presence_required &&
                                            <p className="text-right mt-10 mb-0 bold">{t('medicalConsultations.prescriptionsModal.assistanceRequirement')}</p>}
                                        </Col>
                                      </Row>
                                    </Col>
                                  </Row>
                                </Col>
                              );
                            })
                          }
                        </Row>
                        <Row justify="end">
                          {!countrySettings.prescriptionModal.prescriptionOnly[countryCode] &&

                            <Col lg={24} xs={24} className="mt-15">
                              <Row justify="end">
                                <Col lg={12} xs={24}>
                                  <p><strong>Tipo de documento a generar</strong></p>
                                </Col>
                              </Row>
                            </Col>}
                          <Col lg={24} xs={24}>
                            <Row justify="end">
                              <Col lg={12} xs={24}>
                                <p className='reevaluation-message' dangerouslySetInnerHTML={{ __html: t('medicalConsultations.prescriptionsModal.reevaluationButtonInstruction', { reevaluationState: reevaluationState?.text }) }} />
                              </Col>
                            </Row>
                          </Col>
                          {!countrySettings.prescriptionModal.prescriptionOnly[countryCode] &&
                            <Col lg={24} xs={24} className="mt-5">
                              <Row justify="end">
                                <Col lg={12} xs={24}>
                                  <Select
                                    placeholder="Seleccione tipo de documento"
                                    className="cmt-select"
                                    style={{ width: 300 }}
                                    onChange={(value) => {
                                      setSelectedPrescription(value);
                                    }}
                                    id="selectType"
                                  >
                                    {
                                      prescriptionTypes.map((type) => {
                                        return (
                                          <Option key={type.key} value={type.key} disabled={type.disabled}>
                                            {type.label}
                                          </Option>
                                        );
                                      })
                                    }
                                  </Select>
                                </Col>
                              </Row>
                            </Col>
                          }
                          <Col lg={24} xs={24} className="mt-15">
                            <Row justify="end">
                              <Col>
                                <Button
                                  loading={loading.sendForm}
                                  htmlType="submit"
                                  className="cmt-button"
                                  disabled={countrySettings.prescriptionModal.prescriptionOnly[countryCode] && status?.status !== 'reproved_temporally'}
                                  type="primary"
                                  id="generatePrescriptionButton"
                                >
                                  {t('medicalConsultations.prescriptionsModal.generatePrescriptionButton')}
                                </Button>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Form>
                    )}
                </Col>
              </Row>
            </Col>
          </Fragment>
        )}
      </Row>
      {drawerExamIds.length > 0 && (
        <ConsultationDiagnosticExamDrawer
          open={drawerVisible}
          onClose={() => setDrawerVisible(false)}
          record={drawerRecord}
        />
      )}
    </Modal>
  );
};

export default PrescriptionsModal;